import React from "react";
import { toastUtils } from "../../../utils/toast-utils";
import { Button } from "@vitalsource/vstui";
import { cloud } from "@vitalsource/vstui/colors";
import BookStore from "../../../stores/book-store";
import Dispatcher from "../../../dispatcher";
import CandyStripe from "../../../ui/apps/library/candystripe";

const createFrame = printPath => {
  const printFrame = $(
    `<iframe class='print-frame' aria-hidden='true' src='${Fraction.Helpers.addParameter(
      printPath,
      "brand",
      Fraction.Brand.name
    )}' />`
  );
  $("body").append(printFrame);
  return printFrame;
};

const renderInProgress = theme => (
  <div style={{ position: "relative" }} className="print-pages-toast">
    <div style={{ marginBottom: 10 }}>
      {i18n.t("print.preparing-print-job")}
    </div>
    <CandyStripe
      style={{ position: "relative" }}
      theme={{ color: { secondary: theme.vstui.color.primary } }}
    />
  </div>
);

const renderDone = (printId, printFrame) => (
  <div className="print-pages-toast">
    {i18n.t("print.print-job-ready")}{" "}
    <Button
      inline
      label={i18n.t("print.print-now")}
      style={{ marginLeft: 15 }}
      onClick={() => {
        toastUtils.dismiss(`print-toast-${printId}`);
        printFrame.get(0).contentWindow.postMessage("print", "*");
      }}
    >
      {i18n.t("print.print-now")}
    </Button>
  </div>
);

const notifyPrintJob = (book, theme, printId, from, to, payload, onSuccess) => {
  printId = printId || Fraction.Helpers.generateGUID();
  const { notify } = toastUtils;
  const opts = {
    id: `print-toast-${printId}`,
    title: i18n.t("print.print-job-title", { from, to }),
    imageUrl: "/img/print.svg",
    backgroundColor: theme.vstui.color.white,
    borderColor: cloud.getShade(7),
    borderPosition: "left",
    maxWidth: 250,
    horizontalPosition: "right",
    dismissible: true
  };

  const initPrintFrame = printPath => {
    const printFrame = createFrame(printPath);
    printFrame.on("load", () => {
      if (Fraction.Brand.Print.mimeo) {
        onSuccess();
        notify({ ...opts, children: renderDone(printId, printFrame) });
      } else {
        Fraction.Helpers.removeLoader("#bookshelf");
      }
    });
  };

  if (Fraction.Brand.Print.mimeo) {
    notify({ ...opts, children: renderInProgress(theme) });
    if (Fraction.Brand.Print.async) {
      pollPrintRequest(book, payload.requestId)
        .then(() => claimPrintRequestPath(book, payload.requestId))
        .then(printPath => initPrintFrame(printPath));
    } else {
      initPrintFrame(payload.printPath);
    }
  } else {
    initPrintFrame(payload.printPath);
    Fraction.Helpers.addLoader(
      "#bookshelf",
      "background:transparent;cursor:progress;cursor:wait;",
      ""
    );
  }
};

const pollPrintRequest = function (book, requestId) {
  // The possible states for 'status' are: ready, printing, failed, done, and timeout.
  // 'ready' and 'printing' are the only states that signifies a print request is still in progress,
  // the other states would indicate that the print request has finished, successfully or unsuccessfully.
  const inProgressStates = ["ready", "printing"];
  const interval = 5000;

  return new Promise(resolve => {
    const callback = response => {
      let status = response.data.status && response.data.status.toLowerCase();
      if (response.isSuccess() && inProgressStates.includes(status)) {
        setTimeout(
          () => book.viewer.Book.mimeoGetPrintRequest(requestId, callback),
          interval
        );
      } else {
        resolve();
      }
    };
    book.viewer.Book.mimeoGetPrintRequest(requestId, callback);
  });
};

const claimPrintRequestPath = (book, requestId) => {
  return new Promise((resolve, reject) => {
    const opts = {
      post_message_print: true,
      auto_print: false,
      brand: Fraction.Brand.name,
      locale: Fraction.User.locale
    };
    book.viewer.Book.mimeoClaimPrintRequest(requestId, opts, response => {
      if (response.isSuccess()) {
        resolve(response.data);
      } else {
        reject(response);
      }
    });
  });
};

const executePrintJob = (theme, printId, isbn, from, to) => {
  const book = BookStore.get(isbn);
  const payload = { printId, isbn, from, to };

  Dispatcher.emit(Dispatcher.PRINT_START, payload);

  const onSuccess = () => Dispatcher.emit(Dispatcher.PRINT_DONE, payload);

  const onFailure = error => {
    Dispatcher.emit(Dispatcher.PRINT_FAILED, payload);
    error.data.code === 401 || error.code === 401
      ? Fraction.Helpers.signin()
      : Fraction.Helpers.codeError();
  };

  const callback = response => {
    if (response.isSuccess()) {
      let printPayload = { printPath: response.data };
      if (Fraction.Brand.Print.mimeo && Fraction.Brand.Print.async) {
        printPayload = response.data;
      }
      notifyPrintJob(book, theme, printId, from, to, printPayload, onSuccess);
    } else {
      onFailure(response);
    }
  };

  if (Fraction.Brand.Print.mimeo) {
    const opts = {
      post_message_print: true,
      auto_print: false,
      brand: Fraction.Brand.name,
      locale: Fraction.User.locale
    };
    if (Fraction.Brand.Print.async) {
      book.viewer.Book.mimeoPrintPageRangeAsync(from, to, opts, callback);
    } else {
      book.viewer.Book.mimeoPrintPageRange(from, to, opts, callback);
    }
  } else {
    book.viewer.Book.printPageRange(from, to, callback);
  }
  return printId;
};

export default executePrintJob;
